// Client-side GPX parsing: the backend only stores the uploaded file, it does
// not extract distance/elevation/duration, so we derive them here (native
// DOMParser, no dependency) to auto-fill the activity form.

const EARTH_RADIUS_M = 6371000;

function haversine(lat1, lon1, lat2, lon2) {
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  return 2 * EARTH_RADIUS_M * Math.asin(Math.sqrt(a));
}

/**
 * Parses a .gpx file's text into { latitude, longitude, distanceKm,
 * elevationGainM, elevationLossM, minAltitudeM, maxAltitudeM, durationHours,
 * profile }.
 * Any value that can't be derived (e.g. no <time> tags) is omitted.
 */
export function parseGpx(xmlText) {
  const doc = new DOMParser().parseFromString(xmlText, 'application/xml');
  if (doc.querySelector('parsererror')) {
    throw new Error('El archivo GPX no es válido.');
  }

  const points = Array.from(doc.querySelectorAll('trkpt, rtept, wpt')).map((pt) => ({
    lat: Number(pt.getAttribute('lat')),
    lon: Number(pt.getAttribute('lon')),
    ele: pt.querySelector('ele') ? Number(pt.querySelector('ele').textContent) : null,
    time: pt.querySelector('time') ? new Date(pt.querySelector('time').textContent) : null,
  }));

  const result = {};

  const first = points.find((p) => Number.isFinite(p.lat) && Number.isFinite(p.lon));
  if (first) {
    result.latitude = first.lat;
    result.longitude = first.lon;
  }

  if (points.length >= 2) {
    let distanceM = 0;
    for (let i = 1; i < points.length; i++) {
      distanceM += haversine(
        points[i - 1].lat,
        points[i - 1].lon,
        points[i].lat,
        points[i].lon
      );
    }
    result.distanceKm = Math.round((distanceM / 1000) * 100) / 100;
  }

  const elevations = points.map((p) => p.ele).filter((e) => Number.isFinite(e));
  if (elevations.length >= 2) {
    let gain = 0;
    let loss = 0;
    for (let i = 1; i < elevations.length; i++) {
      const diff = elevations[i] - elevations[i - 1];
      if (diff > 0) gain += diff;
      else loss -= diff;
    }
    result.elevationGainM = Math.round(gain);
    result.elevationLossM = Math.round(loss);
    result.minAltitudeM = Math.round(Math.min(...elevations));
    result.maxAltitudeM = Math.round(Math.max(...elevations));
    result.profile = elevations;
  }

  const times = points.map((p) => p.time).filter((t) => t instanceof Date && !Number.isNaN(t));
  if (times.length >= 2) {
    const ms = times[times.length - 1] - times[0];
    if (ms > 0) result.durationHours = Math.round((ms / 3600000) * 100) / 100;
  }

  return result;
}

// Alias groups matching a parsed GPX datum to whichever field key the backend
// happens to use for a given activity type (mirrors src/activities/activityTypes.js).
export const GPX_FIELD_ALIASES = {
  latitude: ['latitude', 'lat'],
  longitude: ['longitude', 'lon', 'lng'],
  distanceKm: ['distance', 'distancia'],
  elevationGainM: ['gain', 'desnivel', 'desnivelPositivo'],
  elevationLossM: ['loss', 'desnivelNegativo'],
  durationHours: ['time', 'tiempo', 'duracion'],
  minAltitudeM: ['minAltitude', 'cotaMinima', 'altMin', 'minAlt'],
  maxAltitudeM: ['maxAltitude', 'cotaMaxima', 'altMax', 'maxAlt'],
  profile: ['profile', 'elevationProfile', 'altitudeProfile', 'perfil', 'elevations', 'altitudes'],
};

export const GPX_ALLOWED_SLUGS = ['senderismo', 'ciclismo'];

/**
 * Parses a .gpx file's track geometry for display: every trackpoint with its
 * cumulative distance along the route (for an elevation-profile x-axis) plus
 * any named waypoints (for map/profile labels), snapped to the nearest track
 * point. Returns null when there's no usable track (fewer than 2 points).
 */
export function parseGpxTrack(xmlText) {
  const doc = new DOMParser().parseFromString(xmlText, 'application/xml');
  if (doc.querySelector('parsererror')) {
    throw new Error('El archivo GPX no es válido.');
  }

  const points = Array.from(doc.querySelectorAll('trkpt, rtept'))
    .map((pt) => ({
      lat: Number(pt.getAttribute('lat')),
      lon: Number(pt.getAttribute('lon')),
      ele: pt.querySelector('ele') ? Number(pt.querySelector('ele').textContent) : null,
    }))
    // A point missing its lat/lon attribute reads as (0,0) rather than NaN,
    // which would silently blow up the map bounds to include the middle of
    // the ocean — drop anything that isn't a real coordinate.
    .filter((p) => Number.isFinite(p.lat) && Number.isFinite(p.lon) && (p.lat !== 0 || p.lon !== 0));
  if (points.length < 2) return null;

  let distanceM = 0;
  points[0].distKm = 0;
  for (let i = 1; i < points.length; i++) {
    distanceM += haversine(points[i - 1].lat, points[i - 1].lon, points[i].lat, points[i].lon);
    points[i].distKm = distanceM / 1000;
  }

  const named = Array.from(doc.querySelectorAll('wpt')).map((wpt) => ({
    lat: Number(wpt.getAttribute('lat')),
    lon: Number(wpt.getAttribute('lon')),
    name: wpt.querySelector('name')?.textContent?.trim() || '',
  }));

  // Snap each named waypoint to its nearest track point so it can be placed on
  // both the map and the elevation profile (by that point's index/distance).
  const waypoints = named
    .filter((w) => w.name && Number.isFinite(w.lat) && Number.isFinite(w.lon))
    .map((w) => {
      let bestIdx = 0;
      let bestDist = Infinity;
      points.forEach((p, i) => {
        const d = haversine(w.lat, w.lon, p.lat, p.lon);
        if (d < bestDist) {
          bestDist = d;
          bestIdx = i;
        }
      });
      return { name: w.name, index: bestIdx, distKm: points[bestIdx].distKm, lat: points[bestIdx].lat, lon: points[bestIdx].lon, ele: points[bestIdx].ele };
    });

  const elevations = points.map((p) => p.ele).filter(Number.isFinite);
  return {
    points,
    waypoints,
    hasElevation: elevations.length >= 2,
  };
}
